import React, { CSSProperties, ReactNode, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import Footer from 'src/components/Footer'
import { PageContext } from 'src/page/types'
import {
    MyContext,
    MyContextConsumer,
    MyContextProvider
} from 'src/components/MyContext'
import { RenderCb } from 'src/components/ui/RenderCb'
import { getAllPages } from 'src/page/pages'
import Topbar from 'src/navigation/Topbar'
import { IntlProvider } from 'react-intl'
import { flatResources } from 'src/i18n/translation/resources'
import { t } from 'src/i18n/translation/t'
import { getImage } from 'gatsby-plugin-image'
import { Meta, shareImage } from 'src/components/Meta'
import useSiteMetadata from 'src/components/SiteMetadata'
import { Lang } from 'src/i18n/lang/types'
import { useEnv } from 'src/hooks/useEnv'
import CookieBar from 'src/components/cookiebar'
import { absoluteUrl } from 'src/util/url'
import AnnouncementBanner from 'src/components/AnnouncementBanner'
import announcementBanner from 'src/config/announcementBanner.json'
import { pageNames } from 'src/pageNames'
import { alignContactForm, contactFormId } from 'src/util/alignContactForm'

// og:locale expects a `language_TERRITORY` value, plain 'sk'/'en'/'de' is invalid
const ogLocales: { [key: string]: string } = {
    sk: 'sk_SK',
    en: 'en_US',
    de: 'de_DE'
}

const StaticMeta = ({ lang }: { lang: Lang }) => {
    const SITE_NAME = 'Garwan.sk'
    const { favicon } = useSiteMetadata()
    const { isProd } = useEnv()
    return (
        <Helmet>
            <html key="meta-lang" lang={lang} />
            <meta charSet="utf-8" />
            <meta
                name="viewport"
                content="width=device-width,initial-scale=1"
            />
            <meta name="author" content={SITE_NAME} />
            <meta property="og:locale" content={ogLocales[lang] || lang} />
            <meta property="og:type" content="website" />
            <meta property="og:site_name" content={SITE_NAME} />
            <meta
                name="robots"
                content={isProd ? 'index,follow' : 'no-index, no-follow'}
            />
            <meta name="twitter:card" content="summary_large_image" />
            <link
                rel="icon"
                type="image/png"
                href={getImage(favicon)?.images?.fallback?.src}
                sizes="16x16"
            />
            <meta name="theme-color" content="#fff" />
        </Helmet>
    )
}

type Layout = {
    children: RenderCb<{ ctx: MyContext }> | ReactNode
    overlayNav?: boolean
    pageCtx: PageContext
}

const Layout = ({ children, pageCtx, overlayNav = false }: Layout) => {
    const [bannerHeight, setBannerHeight] = useState(0)
    useEffect(() => alignContactForm(false), [bannerHeight])
    const allPages = getAllPages()
    const myContext = MyContext.create(pageCtx, allPages)

    const lang = pageCtx.lang
    const messages = flatResources[pageCtx.lang]
    const title = t(messages['meta.title'])
    const description = t(messages['meta.desc'])
    const { siteUrl } = useSiteMetadata()

    return (
        <div>
            <StaticMeta lang={lang} />
            <Meta
                title={title}
                description={description}
                image={absoluteUrl(siteUrl, shareImage.path)}
                url={absoluteUrl(siteUrl, pageCtx.path)}
            />
            <IntlProvider locale={lang} messages={messages}>
                <MyContextProvider value={myContext}>
                    <div
                        style={
                            {
                                '--announcement-banner-height': `${bannerHeight}px`
                            } as CSSProperties
                        }
                    >
                        {announcementBanner.enabled && (
                            <AnnouncementBanner
                                id={announcementBanner.id}
                                heading={
                                    pageCtx.msgs.announcementBanner.heading
                                }
                                text={pageCtx.msgs.announcementBanner.text}
                                linkText={
                                    pageCtx.msgs.announcementBanner.linkText
                                }
                                closeLabel={
                                    pageCtx.msgs.announcementBanner.close
                                }
                                to={`${myContext.nav.linkTo(
                                    pageNames.contact
                                )}#${contactFormId}`}
                                onHeightChange={setBannerHeight}
                                startsAt={announcementBanner.startsAt}
                                endsAt={announcementBanner.endsAt}
                            />
                        )}
                        <Topbar
                            lang={pageCtx.lang}
                            overlayContent={overlayNav}
                        />
                        <MyContextConsumer>
                            {(ctx) =>
                                typeof children === 'function'
                                    ? children({ ctx })
                                    : children
                            }
                        </MyContextConsumer>
                        <Footer lang={pageCtx.lang} msgs={pageCtx.msgs} />
                        <CookieBar pageContext={pageCtx} />
                    </div>
                </MyContextProvider>
            </IntlProvider>
        </div>
    )
}

export default Layout
